import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
  EntityType,
  RelatedAs,
} from "../../../api/stakeholder_relationships";
import { KycType } from "../../../api/enrichedStakeholder";
import { createStakeholders } from "../logic/stakeholderThunks";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import { getAccountProductInfoByAccountNumber } from "../../creditAccounts/logic/slice";
import {
  maskAadhaarForStorage,
  validateIdentifier,
} from "../../../components/ui/formelement/v2/EditableField";

import ManagementFormField from "./ManagementFormField";
import ManagementModal from "../../../components/ui/modals/ManagementModal";

interface AddNewKeyPersonnelModalProps {
  accountNumber: string;
  primaryBorrowerStakeholderId: string;
  relatedAs: RelatedAs;
  onClose: () => void;
}

interface FormState {
  name: string;
  pan: string;
  din: string;
  aadhaar: string;
}

const AddNewKeyPersonnelModal: React.FC<AddNewKeyPersonnelModalProps> = ({
  accountNumber,
  primaryBorrowerStakeholderId,
  relatedAs,
  onClose,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const displayAccountNumber = useAppSelector(
    (state) =>
      getAccountProductInfoByAccountNumber(state, accountNumber)
        ?.ext_account_number || "--",
  );

  const [form, setForm] = useState<FormState>({
    name: "",
    pan: "",
    din: "",
    aadhaar: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid = form.name.trim().length > 0;

  const isPanValid = validateIdentifier("pan", form.pan) === null;

  const isAadhaarValid = validateIdentifier("aadhaar", form.aadhaar) === null;

  const isDinValid = validateIdentifier("din", form.din) === null;

  const handleSubmit = async () => {
    setIsSubmitted(true);

    if (!caseId || !isFormValid || isLoading) {
      return;
    }

    if (!isPanValid || !isAadhaarValid || !isDinValid) {
      return;
    }

    try {
      setIsLoading(true);

      const kycDetails = [];

      if (form.pan.trim()) {
        kycDetails.push({
          kyc_id: uuidv4(),
          kyc_type: KycType.PAN,
          kyc_value: form.pan.trim().toUpperCase(),
        });
      }

      if (form.aadhaar.trim()) {
        kycDetails.push({
          kyc_id: uuidv4(),
          kyc_type: KycType.AADHAAR,
          kyc_value: maskAadhaarForStorage(form.aadhaar),
        });
      }

      await dispatch(
        createStakeholders({
          caseId,
          request: {
            stakeholders: [
              {
                stakeholder: {
                  name: form.name.trim(),
                },
                relationships: [
                  {
                    entity_type: EntityType.STAKEHOLDER,
                    entity_id: primaryBorrowerStakeholderId,
                    related_as: relatedAs,
                  },
                ],
                business_key_personnel_details: form.din.trim()
                  ? {
                      din: form.din.trim(),
                    }
                  : undefined,
                ...(kycDetails.length > 0
                  ? {
                      kyc_details: kycDetails,
                    }
                  : {}),
              },
            ],
          },
        }),
      ).unwrap();
      showSuccessToast("Key personnel added successfully.");
      onClose();
    } catch {
      showErrorToast("Failed to add key personnel. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onClose();
  };

  return (
    <ManagementModal
      title="Add Director / Partner / Proprietor"
      subtitleClassName="text-[12px] leading-4 text-[#737373]"
      subtitle={displayAccountNumber}
      onSubmit={handleSubmit}
      onClose={handleClose}
      submitLabel="Add"
      submitEnabled={isFormValid}
      isLoading={isLoading}
      className="w-[661px]"
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-4">
          <ManagementFormField
            label="Full Name"
            value={form.name}
            onChange={(value) => handleChange("name", value)}
            required
            disabled={isLoading}
            showError={isSubmitted}
          />

          <ManagementFormField
            label="PAN"
            type="pan"
            value={form.pan}
            onChange={(value) => handleChange("pan", value)}
            disabled={isLoading}
            showError={isSubmitted}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ManagementFormField
            label="DIN Number"
            type="din"
            value={form.din}
            onChange={(value) => handleChange("din", value)}
            disabled={isLoading}
            showError={isSubmitted}
          />

          <ManagementFormField
            label="Aadhaar Number"
            type="aadhaar"
            value={form.aadhaar}
            onChange={(value) => handleChange("aadhaar", value)}
            disabled={isLoading}
            showError={isSubmitted}
          />
        </div>
      </div>
    </ManagementModal>
  );
};

export default AddNewKeyPersonnelModal;